import React, { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { getApiBase } from '../lib/utils'
import ConfigPageLayout from '../components/ConfigPageLayout'

type Props = {
  onBrandingUpdate?: (url: string | null) => void
}

export default function BrandingConfigPage({ onBrandingUpdate }: Props) {
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const apiBase = getApiBase()

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/global-config')
      const rawUrl = data.branding_logo_url
      if (!rawUrl) {
        setLogoUrl(null)
      } else {
        const normalized = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`
        const fullUrl = `${apiBase}${normalized}`
        setLogoUrl(fullUrl)
        try {
          localStorage.setItem('branding_logo_url', fullUrl)
        } catch {}
      }
    } catch (err: any) {
      setError(err?.message || 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }, [apiBase])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      setUploading(true)
      setStatus(null)
      setError(null)

      const formData = new FormData()
      formData.append('file', file)

      try {
        const { data } = await api.post('/global-config/logo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const rawUrl = data.logo_url
        const normalized = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`
        const fullUrl = `${apiBase}${normalized}`
        setLogoUrl(fullUrl)
        try {
          localStorage.setItem('branding_logo_url', fullUrl)
        } catch {}
        setStatus('Logo téléversé avec succès ✅')
        if (onBrandingUpdate) onBrandingUpdate(fullUrl)
        window.dispatchEvent(new CustomEvent('branding-logo-updated', { detail: fullUrl }))
      } catch (err: any) {
        const detail = err?.response?.data?.detail
        setError(typeof detail === 'string' ? detail : err?.message || 'Erreur lors du téléversement')
      } finally {
        setUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
    },
    [apiBase, onBrandingUpdate]
  )

  if (loading) {
    return (
      <ConfigPageLayout title="BRANDING" description="Gestion du logo et de l'identité visuelle">
        <div className="flex items-center justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      </ConfigPageLayout>
    )
  }

  return (
    <ConfigPageLayout title="BRANDING" description="Gestion du logo et de l'identité visuelle">
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-6 rounded-3xl border border-emerald-100/80 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Aperçu du logo</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Téléversez un logo pour remplacer l'identité visuelle Totemium dans l'en-tête de l'application.
          </p>
          <div className="flex items-center justify-center rounded-2xl border border-dashed border-emerald-200/70 bg-emerald-50/50 p-10 text-center dark:border-emerald-900/60 dark:bg-emerald-900/20">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo personnalisé" className="max-h-28 object-contain" />
            ) : (
              <div className="space-y-2">
                <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-2xl font-semibold text-white shadow-lg">
                  TM
                </span>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">Aucun logo personnalisé</p>
              </div>
            )}
          </div>
          <ul className="space-y-2 text-sm text-zinc-500 dark:text-zinc-400">
            <li>• Dimensions recommandées: 128×128px minimum</li>
            <li>• Poids conseillé: &lt; 1 Mo</li>
            <li>• Formats: PNG, JPG, SVG, WebP</li>
          </ul>
        </section>

        <section className="space-y-6 rounded-3xl border border-zinc-200/70 bg-white/95 p-8 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/90">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Téléverser un logo</h2>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-300">Logo principal</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp,image/gif"
              onChange={handleFileChange}
              className="w-full cursor-pointer rounded-xl border border-emerald-200/70 bg-white/80 px-4 py-3 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:bg-zinc-950/80 dark:text-zinc-100"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-full border border-emerald-200/70 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={uploading}
            >
              {uploading ? 'Téléversement…' : 'Choisir un logo'}
            </button>
            <button
              type="button"
              onClick={fetchConfig}
              className="rounded-full border border-zinc-200/70 px-5 py-2.5 text-sm font-medium text-zinc-600 transition hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
              Rafraîchir
            </button>
          </div>

          {status && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
              {status}
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
              {error}
            </div>
          )}
        </section>
      </div>
    </ConfigPageLayout>
  )
}
